import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Clock, Phone, Car, Truck, Store } from "lucide-react";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";

export function Stores() {
  const [stores, setStores] = useState([]);
  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)); }, []);
  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-10">
      <h1 className="text-4xl font-extrabold text-midnight">Find a KRVMart Store</h1>
      <p className="mt-1 text-muted-foreground">Designed for multi-city expansion. Locations shown below are planned — placeholders until stores open.</p>
      <div className="mt-8 grid md:grid-cols-3 gap-5">
        {stores.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.06}>
            <Link to={`/stores/${s.slug}`} data-testid={`store-${s.slug}`} className="block rounded-3xl border border-border bg-card p-6 transition-shadow hover:shadow-lg">
              <div className="flex items-center justify-between"><h3 className="text-lg font-extrabold text-midnight">{s.name}</h3><span className="rounded-full bg-gold/15 px-3 py-1 text-[11px] font-bold uppercase text-gold-dark">{s.status}</span></div>
              <p className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground"><MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {s.address}</p>
              <p className="mt-3 text-xs font-semibold text-midnight">{s.marts.join(" • ")}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function StorePage() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/stores/${slug}`).then((r) => setS(r.data)).catch(() => setS(null)); }, [slug]);
  if (!s) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-[1100px] px-4 md:px-8 py-10">
      <Link to="/stores" className="text-sm text-muted-foreground hover:text-midnight">← All stores</Link>
      <div className="mt-4 flex items-center gap-3">
        <h1 className="text-4xl font-extrabold text-midnight">{s.name}</h1>
        <span className="rounded-full bg-gold/15 px-3 py-1 text-xs font-bold uppercase text-gold-dark">{s.status}</span>
      </div>
      {s.status === "planned" && <p className="mt-2 rounded-xl bg-secondary p-3 text-sm text-muted-foreground">This is a <b>Planned Store Concept</b>. Details are placeholders and will be updated when the store opens.</p>}
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="space-y-4 rounded-3xl border border-border p-6">
          {[[MapPin, "Address", s.address], [Clock, "Hours", s.hours], [Phone, "Phone", s.phone]].map(([Icon, t, v]) => (
            <div key={t} className="flex items-start gap-3"><Icon className="mt-0.5 h-5 w-5 text-gold-dark" /><div><p className="text-sm font-bold text-midnight">{t}</p><p className="text-sm text-muted-foreground">{v}</p></div></div>
          ))}
          <div className="flex items-center gap-3"><Car className="h-5 w-5 text-gold-dark" /><span className="text-sm text-muted-foreground">{s.parking ? "Parking available" : "No parking"}</span></div>
        </div>
        <div className="rounded-3xl border border-border p-6">
          <p className="text-sm font-bold text-midnight mb-3">Available Marts</p>
          <div className="flex flex-wrap gap-2 mb-5">{s.marts.map((m) => <span key={m} className="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold">{m}</span>)}</div>
          <p className="text-sm font-bold text-midnight mb-3">Services</p>
          <div className="space-y-2">{s.services.map((sv) => <p key={sv} className="flex items-center gap-2 text-sm text-muted-foreground"><Truck className="h-4 w-4 text-gold-dark" /> {sv}</p>)}</div>
        </div>
      </div>
      <div className="mt-6 overflow-hidden rounded-3xl border border-border">
        <iframe title="map" className="h-72 w-full" loading="lazy" src={`https://maps.google.com/maps?q=${encodeURIComponent(s.map_query)}&output=embed`} />
      </div>
    </div>
  );
}
